import './RoleStageActionFlows.scss';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import { TestKind } from 'enums/tests';
import {
  SupportedLocale,
  useStartEnglishProficiencyIntegrityMonitoringSessionMutation,
  useUserQuery,
} from 'generated/graphql';
import { useIntegrityCamera } from 'hooks/useIntegrityCamera';
import { StartIntegritySession } from 'hooks/useIntegrityMonitoring';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@spotted-zebra-uk/ui-components';
import config, {
  withIntegrityMonitoringNote,
} from 'views/Test/TestInfo/TestInfo.constants';
import { ThankYouTemplate } from 'components/organisms/ThankYouTemplate/ThankYouTemplate';
import { ThankYouPageContentType } from 'components/organisms/ThankYouTemplate/ThankYouTemplate.enums';
import EnglishProficiencyPractice from 'views/Test/Tests/EnglishProficiencyTest/EnglishProficiencyPractice';
import EnglishProficiencyTestRunner from './EnglishProficiencyTestRunner';
import { actionCandidateStatusToTestStatus } from './RoleStageActionFlows.helpers';
import { useOnlineAssessmentIntegrityMonitoring } from './useOnlineAssessmentIntegrityMonitoring';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';
import { getCompanyLogoUrl } from 'views/CandidatePortal/CandidatePortal.helpers';

/** The practice legs replace nothing on the server — see the note below. */
type TPhase = 'intro' | 'practice' | 'practiceComplete' | 'test';

/**
 * intro → (practice intro → practice run → practice complete) → the sitting.
 *
 * The practice leg is entirely in the browser: two items written into the
 * client, no clock, nothing saved and no score. The bank is a fixed purchased
 * form with no spare items, so a practice drawn from it would either burn real
 * questions or need content nobody has bought — and none of that is what a
 * candidate needs here. What they need is to have met the page once: that a
 * chosen option is written into the gap, that a typed one is typed into it,
 * that answering marks the card. Those are the page's, not the content's.
 *
 * The test is only ever in English, so there is no locale to choose. Leaving
 * `selectedLocales` unset keeps the intro's language picker away; the
 * candidate's own preferred locale is still passed, because that is what makes
 * `TestIntro` show the English-only notice to someone who reads the portal in
 * another language.
 */
const RoleStageEnglishProficiencyTest: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, stage, loading, exiting, actionsUrl, thankYouUrl } =
    useRoleStageActionsFlow();

  const [phase, setPhase] = useState<TPhase>('intro');
  const [showPracticeTestIntro, setShowPracticeTestIntro] = useState(false);

  const userQueryResponse = useUserQuery();
  const preferredLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  const action = stage?.actions.englishProficiencyAction;

  // Camera-based integrity monitoring (project toggle): the camera is
  // requested on the intro page; the session starts on grant and runs until
  // this flow unmounts.
  const monitoringEnabled =
    (stage?.isIntegrityMonitoringEnabled ?? false) && !!action;
  const camera = useIntegrityCamera(monitoringEnabled);
  const [startIntegritySession] =
    useStartEnglishProficiencyIntegrityMonitoringSessionMutation();
  const actionId = action?.id;
  const startSession = useCallback<StartIntegritySession>(
    async input => {
      if (!actionId) return null;
      const { data } = await startIntegritySession({
        variables: {
          englishProficiencyAssessmentStageActionCandidateId: actionId,
          input,
        },
      });
      return data?.startEnglishProficiencyIntegrityMonitoringSession ?? null;
    },
    [actionId, startIntegritySession]
  );
  useOnlineAssessmentIntegrityMonitoring({
    enabled: monitoringEnabled,
    stream: camera.stream,
    startSession,
  });

  // The stage has no English proficiency test — nothing to run here.
  useEffect(() => {
    if (stage && !action) {
      navigate(actionsUrl);
    }
  }, [stage, action, navigate, actionsUrl]);

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !role || !stage || !action) {
    return null;
  }

  if (phase === 'practice') {
    return (
      <EnglishProficiencyPractice
        onFinishPractice={() => setPhase('practiceComplete')}
      />
    );
  }

  if (phase === 'practiceComplete') {
    // Mirrors the cognitive tests' panel: on to the real thing, or back to the
    // instructions to read them again.
    return (
      <ThankYouTemplate
        onClick={() => setPhase('test')}
        mainText={t('stage.testCallback.youHaveCompletedYourPracticeTest')}
        contentType={ThankYouPageContentType.FINISHED_PRACTICE_STAGE}
        title={t('stage.testCallback.testPracticeCompleted')}
        buttonText={t('test.instructions.general.startNow')}
        companyLogoUrl={getCompanyLogoUrl(role.company)}
        companyName={role.company.name}
        isPracticeTest
        onReturn={() => {
          setShowPracticeTestIntro(false);
          setPhase('intro');
        }}
      />
    );
  }

  if (phase !== 'test') {
    const singleTestConfig = config[TestKind.EnglishProficiency]({
      timeRequirement: action.allowedTimeSeconds,
    });
    const notes = monitoringEnabled
      ? withIntegrityMonitoringNote(singleTestConfig.notes)
      : singleTestConfig.notes;
    const MainIcon = singleTestConfig.icon;

    return (
      <Container className="TestInfoContainer">
        {!showPracticeTestIntro && (
          <NavbarWithBackLink
            to={actionsUrl}
            ariaLabel={t('assessment.returnToAllAssessmentActions')}
            label={t('assessment.actions')}
          />
        )}
        <HelmetAndPageAnnouncer
          pageTitle={t('test.introForTest', {
            actionName: singleTestConfig.title,
          })}
        />
        <TestIntro
          MainIcon={MainIcon}
          type={singleTestConfig.key}
          assessmentLocale={preferredLocale}
          companyName={role.company.name}
          title={singleTestConfig.title}
          notes={notes}
          testStatus={actionCandidateStatusToTestStatus(action.status)}
          hasPractice
          onStart={() => setPhase('test')}
          onStartPracticeTest={() => setPhase('practice')}
          showPracticeTestIntro={showPracticeTestIntro}
          displayPracticeTestIntro={setShowPracticeTestIntro}
          testTimePerQuestion={0}
          cameraPermission={
            monitoringEnabled
              ? { granted: camera.granted, request: camera.request }
              : undefined
          }
        />
      </Container>
    );
  }

  return (
    <EnglishProficiencyTestRunner
      actionCandidateId={action.id}
      onFinishTest={() => navigate(thankYouUrl)}
    />
  );
};

export default RoleStageEnglishProficiencyTest;
